/**
 * AdminStats Component
 *
 * Dashboard metrics component for admin panel displaying system-wide statistics
 * including question counts, context counts, level distribution, and activity overview.
 *
 * Features:
 * - Real-time statistics from API
 * - Level distribution visualization with progress bars
 * - Recent activity feed
 * - Responsive grid layout for metrics cards
 *
 * @module AdminStats
 */

import type React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3,
  TrendingUp,
  FileQuestion,
  Clock,
  Award,
  Activity,
  Loader2,
} from 'lucide-react';
import { useAdminDashboardStats } from '@/hooks/useAdminDashboardStats';
import type { AdminDashboardStats } from '@/lib/adminApi';

type AdminStatsData = Required<AdminDashboardStats>;

/**
 * Metric Card Component
 */
interface MetricCardProps {
  readonly title: string;
  readonly value: string | number;
  readonly subtitle: string;
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly loading: boolean;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  loading,
}: MetricCardProps) {
  const renderValue = () => {
    if (loading) {
      return <Loader2 className="h-6 w-6 animate-spin" />;
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground font-ui">{title}</p>
            <p className="text-3xl font-bold text-primary font-ui">
              {renderValue()}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-ui">
              {subtitle}
            </p>
          </div>
          <Icon className="h-8 w-8 text-primary" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Level Distribution Card Component
 */
interface LevelDistributionCardProps {
  readonly levelDistribution: Array<{
    level: string;
    count: number;
    percentage: number;
  }>;
  readonly loading: boolean;
}

function LevelDistributionCard({
  levelDistribution,
  loading,
}: LevelDistributionCardProps) {
  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8 text-muted-foreground font-ui">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      );
    }

    if (levelDistribution.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground font-ui">
          No questions found
        </div>
      );
    }

    return levelDistribution.map((level) => (
      <div key={level.level} className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-ui">
              {level.level}
            </Badge>
            <span className="text-sm font-medium font-ui">
              {level.count} questions
            </span>
          </div>
          <span className="text-sm text-muted-foreground font-ui">
            {level.percentage}%
          </span>
        </div>
        <Progress value={level.percentage} className="h-2" />
      </div>
    ));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-ui">
          <BarChart3 className="h-5 w-5" />
          Question Level Distribution
        </CardTitle>
        <CardDescription className="font-ui">
          Distribution of questions across CEFR levels
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">{renderContent()}</CardContent>
    </Card>
  );
}

/**
 * Question Statistics Card Component
 */
interface QuestionStatisticsCardProps {
  readonly stats: AdminStatsData;
  readonly loading: boolean;
}

function QuestionStatisticsCard({
  stats,
  loading,
}: QuestionStatisticsCardProps) {
  const renderStatRow = (
    label: string,
    value: number,
    maxValue: number,
  ) => (
    <div>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium font-ui">{label}</span>
        <span className="text-sm text-muted-foreground font-ui">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            value
          )}
        </span>
      </div>
      <Progress
        value={loading ? 0 : Math.min((value / maxValue) * 100, 100)}
        className="h-2"
      />
    </div>
  );

  const renderMostQuestionsLevel = () => {
    if (loading) {
      return <Loader2 className="h-6 w-6 animate-spin mx-auto" />;
    }
    if (stats.levelDistribution.length > 0) {
      return stats.levelDistribution[0]?.level;
    }
    return 'N/A';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-ui">
          <Activity className="h-5 w-5" />
          Question Statistics
        </CardTitle>
        <CardDescription className="font-ui">
          Key question metrics and performance indicators
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {renderStatRow('Total Questions', stats.totalQuestions, 1000)}
          {renderStatRow('Total Contexts', stats.totalContexts, 100)}
          {renderStatRow('Questions This Week', stats.questionsThisWeek, 50)}
        </div>

        <div className="pt-4 border-t">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-primary font-ui">
                {renderMostQuestionsLevel()}
              </p>
              <p className="text-xs text-muted-foreground font-ui">
                Most Questions Level
              </p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary font-ui">
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                ) : (
                  `${stats.levelDistribution.length}`
                )}
              </p>
              <p className="text-xs text-muted-foreground font-ui">
                Active Levels
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AdminStats Component
 *
 * Main component that displays administrative dashboard statistics.
 * Fetches statistics from API on mount and renders key metrics, level distribution,
 * and recent activity overview.
 */
export default function AdminStats() {
  const { stats, loading } = useAdminDashboardStats();

  const levelDistributionWithPercentages = stats.levelDistribution.map(
    (level) => ({
      ...level,
      percentage:
        stats.totalQuestions > 0
          ? Math.round((level.count / stats.totalQuestions) * 100)
          : 0,
    }),
  );

  const recentActivity = [
    {
      action: 'New user registered',
      user: 'maria.schmidt@example.com',
      time: '2 minutes ago',
    },
    {
      action: 'Question created',
      user: 'admin',
      time: '15 minutes ago',
    },
    {
      action: 'Practice completed',
      user: 'john.doe@example.com',
      time: '1 hour ago',
    },
    {
      action: 'User verified',
      user: 'anna.mueller@example.com',
      time: '2 hours ago',
    },
    {
      action: 'Material uploaded',
      user: 'admin',
      time: '3 hours ago',
    },
  ];

  const getSubtitle = (key: string) => {
    if (loading) return 'Loading...';
    if (key === 'questions') {
      return `${stats.questionsThisWeek} added this week`;
    }
    if (key === 'contexts') return 'Available for questions';
    if (key === 'week') return 'Recent activity';
    return 'CEFR levels covered';
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Questions"
          value={stats.totalQuestions}
          subtitle={getSubtitle('questions')}
          icon={FileQuestion}
          loading={loading}
        />
        <MetricCard
          title="Total Contexts"
          value={stats.totalContexts}
          subtitle={getSubtitle('contexts')}
          icon={Activity}
          loading={loading}
        />
        <MetricCard
          title="Questions This Week"
          value={stats.questionsThisWeek}
          subtitle={getSubtitle('week')}
          icon={TrendingUp}
          loading={loading}
        />
        <MetricCard
          title="Level Distribution"
          value={`${stats.levelDistribution.length} levels`}
          subtitle={getSubtitle('levels')}
          icon={Award}
          loading={loading}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <LevelDistributionCard
          levelDistribution={levelDistributionWithPercentages}
          loading={loading}
        />
        <QuestionStatisticsCard stats={stats} loading={loading} />
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-ui">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
          <CardDescription className="font-ui">
            Latest system activities and updates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div
                key={`${activity.action}-${activity.user}-${activity.time}`}
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 bg-muted/50 rounded-lg gap-2"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium font-ui">
                      {activity.action}
                    </p>
                    <p className="text-xs text-muted-foreground font-ui">
                      {activity.user}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground font-ui ml-5 sm:ml-0">
                  {activity.time}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
